import { useEffect, useState } from "react";
import {
  Input,
  Row,
  Col,
  Segmented,
  Button,
  ConfigProvider,
  message,
} from "antd";
import {
  HeartOutlined,
  MedicineBoxOutlined,
  ExperimentOutlined,
  ScissorOutlined,
  SafetyCertificateOutlined,
  GlobalOutlined,
  CheckCircleOutlined,
  HistoryOutlined,
  FileProtectOutlined,
  UserOutlined,
} from "@ant-design/icons";
import "./index.css";

/* ---------- Reusable field ---------- */
const Field = ({ label, value, onChange, placeholder, disabled, suffix }) => (
  <div>
    <label className="mh-field-label">{label}</label>
    <Input
      value={value || ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      allowClear
      disabled={disabled}
      suffix={suffix}
    />
  </div>
);

const YesNo = ({ label, value, onChange }) => {
  const v = value === "1" ? "Yes" : value === "0" ? "No" : null;

  const cls =
    v === "Yes"
      ? "mh-yesno-yes"
      : v === "No"
        ? "mh-yesno-no"
        : "mh-yesno-empty";

  return (
    <div>
      <label className="mh-field-label">{label}</label>
      <Segmented
        block
        className={cls}
        value={v}
        options={[
          { label: "Yes", value: "Yes" },
          { label: "No", value: "No" },
        ]}
        onChange={(val) => onChange(val === "Yes" ? "1" : "0")}
      />
    </div>
  );
};

const Section = ({ index, title, description, icon, children }) => (
  <section className="mh-card">
    <header className="mh-card-header">
      <div className="mh-card-icon">{icon}</div>
      <div className="mh-card-heading">
        <h3 className="mh-card-title">
          <span className="mh-card-index">
            {String(index).padStart(2, "0")}
          </span>
          {title}
        </h3>
        {description && <p className="mh-card-desc">{description}</p>}
      </div>
    </header>
    <div className="mh-card-body">{children}</div>
  </section>
);

/* ---------- Main form ---------- */
const HairForm = ({
  state: extState,
  handleChange: extHandle,
  patientName = "New Patient",
}) => {
  const [internal, setInternal] = useState({});
  const state = extState ?? internal;
  const handleChange =
    extHandle ?? ((key, value) => setInternal((p) => ({ ...p, [key]: value })));

  useEffect(() => {
    const height = parseFloat(state.height);
    const weight = parseFloat(state.weight);

    if (height > 0 && weight > 0) {
      const bmi = (weight / (height / 100) ** 2).toFixed(2);

      // Prevent infinite loop
      if (state.bmi !== bmi) {
        handleChange("bmi", bmi);
      }
    } else {
      if (state.bmi !== "") {
        handleChange("bmi", "");
      }
    }
  }, [state.height, state.weight]);
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "rgb(127, 182, 145)",
          colorInfo: "rgb(127, 182, 145)",
          colorText: "rgb(127, 182, 145)",
          colorBorder: "#e3e8ef",
          borderRadius: 8,
        },
      }}
    >
      <div className="mh-page">
        <div className="mh-container">
          {/* 1. Hair fall history */}
          <Section
            index={1}
            title="Hair fall history"
            description="Onset, family background, and prior treatments."
            icon={<img src="/assets/icons/hf.webp" width={30} />}
          >
            <Row gutter={[20, 18]}>
              <Col xs={24} md={12}>
                <Field
                  label="Age when hair fall started"
                  placeholder="e.g. 24"
                  value={state.hairFallStartingAge}
                  onChange={(v) => handleChange("hairFallStartingAge", v)}
                />
              </Col>
              <Col xs={24} md={12}>
                <YesNo
                  label="Family history of hair fall"
                  value={state.isFamilyDisease}
                  onChange={(v) => handleChange("isFamilyDisease", v)}
                />
              </Col>
              <Col xs={24}>
                <Field
                  label="Any previous treatments for hair fall"
                  placeholder="Minoxidil, finasteride, PRP…"
                  value={state.anyPreviousTreatments}
                  onChange={(v) => handleChange("anyPreviousTreatments", v)}
                />
              </Col>
            </Row>
          </Section>

          {/* 2. Medical history */}
          <Section
            index={2}
            title="Known Health Conditions"
            description="General health and known conditions."
            icon={<img src="/assets/icons/md.webp" width={30} />}
          >
            <Row gutter={[20, 18]}>
              <Col xs={24}>
                <Field
                  label="General health"
                  placeholder="Describe overall health status"
                  value={state.generalHealth}
                  onChange={(v) => handleChange("generalHealth", v)}
                />
              </Col>
              {[
                ["Diabetes", "isDiabetes"],
                ["Asthma", "isAsthma"],
                ["IHD", "isIHD"],
                ["Hypertension", "isHypertension"],
                ["Hepatitis", "isHepatitis"],
                ["Jaundice", "isJaundice"],
                ["Epilepsy", "isEpilepsy"],
              ].map(([label, key]) => (
                <Col xs={24} sm={12} lg={8} key={key}>
                  <YesNo
                    label={label}
                    value={state[key]}
                    onChange={(v) => handleChange(key, v)}
                  />
                </Col>
              ))}
              <Col xs={12}>
                <YesNo
                  label="Skin / Scalp conditions"
                  value={state.isSkinConditions}
                  onChange={(v) => handleChange("isSkinConditions", v)}
                />
              </Col>
            </Row>
          </Section>

          {/* 3. Surgical & procedural */}
          <Section
            index={3}
            title="Surgical & procedural history"
            description="Past procedures, infections, and trauma."
            icon={<img src="/assets/icons/sp.webp" width={30} />}
          >
            <Row gutter={[20, 18]}>
              <Col xs={24} md={12}>
                <Field
                  label="Previous history of local anesthesia"
                  value={state.localAnesthesiaHistory}
                  onChange={(v) => handleChange("localAnesthesiaHistory", v)}
                />
              </Col>
              <Col xs={24} md={12}>
                <YesNo
                  label="Current infection"
                  value={state.anyCurrentInfection}
                  onChange={(v) => handleChange("anyCurrentInfection", v)}
                />
              </Col>
              <Col xs={24} md={12}>
                <Field
                  label="Hospital admissions"
                  value={state.hospitalAdmissions}
                  onChange={(v) => handleChange("hospitalAdmissions", v)}
                />
              </Col>
              <Col xs={24} md={12}>
                <Field
                  label="Previous surgeries"
                  value={state.previousSurgeries}
                  onChange={(v) => handleChange("previousSurgeries", v)}
                />
              </Col>
              <Col xs={24} md={12}>
                <Field
                  label="Trauma"
                  value={state.trauma}
                  onChange={(v) => handleChange("trauma", v)}
                />
              </Col>
              <Col xs={24} md={12}>
                <Field
                  label="Others"
                  value={state.other}
                  onChange={(v) => handleChange("other", v)}
                />
              </Col>
            </Row>
          </Section>

          {/* 4. Body metrics */}
          <Section
            index={4}
            title="Body metrics"
            description="Patient height and weight."
            icon={<img src="/assets/icons/hw.webp" width={30} />}
          >
            <Row gutter={[20, 18]}>
              <Col xs={24} sm={12}>
                <Field
                  label="Height"
                  placeholder="e.g. 175"
                  value={state.height}
                  onChange={(v) => handleChange("height", v)}
                  suffix="(cm)"
                />
              </Col>
              <Col xs={24} sm={12}>
                <Field
                  label="Weight"
                  placeholder="e.g. 70"
                  value={state.weight}
                  onChange={(v) => handleChange("weight", v)}
                  suffix="(kg)"
                />
              </Col>
              <Col xs={24} sm={24}>
                <Field
                  label="BMI"
                  placeholder=""
                  value={state.bmi}
                  disabled={true}
                  suffix="(kg/m²)"
                />
              </Col>
            </Row>
          </Section>

          {/* 5. Examination */}
          <Section
            index={5}
            title="Examination"
            description="Vitals taken at consultation."
            icon={<img src="/assets/icons/examination.webp" width={30} />}
          >
            <Row gutter={[20, 18]}>
              <Col xs={24} sm={12}>
                <Field
                  label="Pulse (bpm)"
                  placeholder="72"
                  value={state.pulse}
                  onChange={(v) => handleChange("pulse", v)}
                  suffix="min"
                />
              </Col>
              <Col xs={24} sm={12}>
                <Field
                  label="Blood pressure"
                  placeholder="120/80"
                  value={state.BP}
                  onChange={(v) => handleChange("BP", v)}
                  suffix="mmHg"
                />
              </Col>
              <Col xs={24} sm={12}>
                <Field
                  label="BSR (mg/dL)"
                  placeholder="95"
                  value={state.BSR}
                  onChange={(v) => handleChange("BSR", v)}
                  suffix="mg/dl"
                />
              </Col>
              <Col xs={24} sm={12}>
                <Field
                  label="Oxygen Levels (SpO2)"
                  placeholder="Enter oxygen level"
                  value={state.oxygen_Level}
                  onChange={(v) => handleChange("oxygen_Level", v)}
                  suffix="%"
                />
              </Col>
            </Row>
          </Section>

          {/* 6. Labs */}
          <Section
            index={6}
            title="Labs"
            description="Pre-operative screening results."
            icon={<img src="/assets/icons/labtest.webp" width={30} />}
          >
            <Row gutter={[20, 18]}>
              <Col xs={24} md={12}>
                <Field
                  label="Pre-Ops"
                  placeholder="Any pre-operation"
                  value={state.anyPreOps}
                  onChange={(v) => handleChange("anyPreOps", v)}
                />
              </Col>
              <Col xs={24} md={12}>
                <Field
                  label="HIV"
                  placeholder="Result"
                  value={state.HIV}
                  onChange={(v) => handleChange("HIV", v)}
                />
              </Col>
              <Col xs={24} md={12}>
                <Field
                  label="Hepatitis B"
                  placeholder="Result"
                  value={state.hepatitisB}
                  onChange={(v) => handleChange("hepatitisB", v)}
                />
              </Col>
              <Col xs={24} md={12}>
                <Field
                  label="Hepatitis C"
                  placeholder="Result"
                  value={state.hepatitisC}
                  onChange={(v) => handleChange("hepatitisC", v)}
                />
              </Col>
            </Row>
          </Section>

          {/* 7. Medications & lifestyle */}
          <Section
            index={7}
            title="Medications & lifestyle"
            description="Current medication, allergies, and habits."
            icon={<img src="/assets/icons/ml.webp" width={30} />}
          >
            <Row gutter={[20, 18]}>
              {[
                ["Aspirin / NSAIDs", "anyAspirin"],
                ["Steroids", "anySteroids"],
                ["Anti-coagulants", "anyAntiCoagulants"],
              ].map(([label, key]) => (
                <Col xs={24} sm={12} lg={6} key={key}>
                  <YesNo
                    label={label}
                    value={state[key]}
                    onChange={(v) => handleChange(key, v)}
                  />
                </Col>
              ))}

              <Col xs={24} sm={12} lg={6}>
                <YesNo
                  label="Cigarette smoking"
                  value={state.smoking}
                  onChange={(v) => handleChange("smoking", v)}
                />
              </Col>
              <Col xs={24} md={12}>
                <Field
                  label="Antibiotic / TB / anti-viral course"
                  value={state.antibioticCourse}
                  onChange={(v) => handleChange("antibioticCourse", v)}
                />
              </Col>
              <Col xs={24} md={12}>
                <Field
                  label="Drugs Allergies"
                  placeholder="Enter drugs allergies"
                  value={state.drug_allergies}
                  onChange={(v) => handleChange("drug_allergies", v)}
                />
              </Col>
              <Col xs={24} md={12}>
                <Field
                  label="Food Allergies"
                  placeholder="Enter food allergies"
                  value={state.food_allergies}
                  onChange={(v) => handleChange("food_allergies", v)}
                />
              </Col>
              <Col xs={24} md={12}>
                <Field
                  label="Bleeding tendencies"
                  value={state.bleedingTendencies}
                  onChange={(v) => handleChange("bleedingTendencies", v)}
                />
              </Col>
              <Col xs={24} md={12}>
                <Field
                  label="Any other substance use"
                  value={state.otherSubstanceUses}
                  onChange={(v) => handleChange("otherSubstanceUses", v)}
                />
              </Col>
              <Col xs={24} md={12}>
                <Field
                  label="Other Medications"
                  placeholder="Enter other medications"
                  value={state.anyOtherMedications}
                  onChange={(v) => handleChange("anyOtherMedications", v)}
                />
              </Col>
            </Row>
          </Section>

          {/* 8. Travel & notes */}
          <Section
            index={8}
            title="Travel & clinician notes"
            description="Recent and planned travel, plus any free-form notes."
            icon={<img src="/assets/icons/th.webp" width={30} />}
          >
            <Row gutter={[20, 18]}>
              <Col xs={24} md={12}>
                <Field
                  label="Recent travel history"
                  placeholder="Country"
                  value={state.recentTravelHistory}
                  onChange={(v) => handleChange("recentTravelHistory", v)}
                />
              </Col>
              <Col xs={24} md={12}>
                <Field
                  label="Flight date (if intending to travel)"
                  placeholder="DD / MM / YYYY"
                  value={state.upComingTravel}
                  onChange={(v) => handleChange("upComingTravel", v)}
                />
              </Col>
              <Col xs={24}>
                <Field
                  label="Any note"
                  placeholder="Additional remarks"
                  value={state.note}
                  onChange={(v) => handleChange("note", v)}
                />
              </Col>
            </Row>
          </Section>
        </div>
      </div>
    </ConfigProvider>
  );
};

export { HairForm };
export default HairForm;
